import { FC, Fragment } from 'react';
import { Button, Checkbox, Col, Divider, Form, Row, Select, Table } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import DataRow from '@/libs/antd/components/DataRow';
import FleetTitle from '@/libs/antd/components/Title';
import {
	getHeadSurveyDetail,
	submitCHApprovalSurvey,
	SubmitCHApprovalSurveyPayload,
} from '@/libs/fleet-api/services/survey';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { useSelectRejectCategory } from '@/libs/fleet-api/hooks/master-data';
import { useForm } from '@/libs/antd/form';
import { useApp } from '@/App';
import rule from '@/libs/antd/rules';
import TextArea from 'antd/es/input/TextArea';
import { useModals } from '@/components/modals/ModalProvider';
import fetcher from '@/libs/fleet-api/fetcher';
import dayjs from 'dayjs';
import { DocumentItem } from '@/components/modals/DocumentModal';
import GenericErrorMessage from '@/components/GenericErrorMessage';

export type CHSurveyApprovalPageProps = {
	prospectId: string;
};

const CHSurveyApprovalPage: FC<CHSurveyApprovalPageProps> = ({
	prospectId,
}) => {
	const { navigate, notification } = useApp();
	const { data, isLoading, isError } = useQuery({
		queryKey: ['getHeadSurveyDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getHeadSurveyDetail(id);
		},
	});
	const {
		openCustomerDetail,
		openCustomerGroupDetail,
		openDocumentModal,
		openProspectDetail,
		openPraSurveyAnalysisModal,
		openPraSurveyDataModal,
		openMapModal,
	} = useModals();

	const {
		mainInformation,
		surveyInformation,
		approvalHistories,
		customerBusinessDocuments,
		customerLegalDocuments,
		customerReferralDocuments,
	} = data?.data ?? {};

	const surveyParticipants = surveyInformation?.surveyParticipants;

	const { form, value, fieldName } = useForm<SubmitCHApprovalSurveyPayload>();

	const selectRejectCategory = useSelectRejectCategory();
	const submitCHApprovalSurveyMutation = useMutation({
		mutationFn: submitCHApprovalSurvey,
	});
	const handleSubmit = async (values: SubmitCHApprovalSurveyPayload) => {
		try {
			if (values.participants.length < 1) {
				notification.error({
					message: 'Error',
					description: 'Required at least one participant',
				});
				return;
			}
			await submitCHApprovalSurveyMutation.mutateAsync(values);
			notification.success({
				message: 'Success',
				description: 'Approval has been saved',
			});
			navigate('CHSurveyApprovalListPage');
		} catch (error) {
			if (fetcher.isError(error)) {
				const message = await error.json();
				notification.error({
					duration: 20,
					message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
					description: (
						<div>
							{message?.metadata?.debugInfo?.messages &&
							message?.metadata?.debugInfo?.messages.length ? (
								<ul>
									{message?.metadata?.debugInfo?.messages.map((msg: string) => (
										<li>{msg}</li>
									))}
								</ul>
							) : (
								<pre>Opps, Something went wrong</pre>
							)}
						</div>
					),
				});
			}
		}
	};

	if (isError) return <GenericErrorMessage />;

	if (isLoading) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Fragment>
			<FleetTitle>Main Information</FleetTitle>
			<DataRow
				loading={isLoading}
				items={[
					[
						[
							'Nama Customer',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openCustomerDetail(mainInformation!.customerId);
								}}
							>
								{mainInformation?.customerName}
							</a>,
						],
						[
							'Prospect ID',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openProspectDetail(mainInformation!.prospectId);
								}}
							>
								{mainInformation?.prospectId}
							</a>,
						],
					],
					[
						['Kelas Industri', mainInformation?.industrySector.industryClass],
						['Sumber Order', mainInformation?.orderSource],
					],
					[
						[
							'Portofolio Guideline',
							mainInformation?.industrySector.portofolioGuideline,
						],
						[
							'Group Customer BMPP',
							mainInformation?.customerGroup?.id ? (
								<a
									href="#"
									className="trigger-popup"
									onClick={(e) => {
										e.preventDefault();
										openCustomerGroupDetail(mainInformation!.customerGroup.id);
									}}
									aria-disabled={!mainInformation?.customerGroup.value}
								>
									{mainInformation?.customerGroup.value ?? '-'}
								</a>
							) : (
								'-'
							),
						],
					],
					[
						[
							'Dokumen',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();

									const documents: DocumentItem[] = [];
									const getDocumentType = (name: string) => {
										if (name.endsWith('pdf')) return 'pdf';
										if (name.endsWith('jpeg')) return 'jpeg';
										if (name.endsWith('jpg')) return 'jpg';
										if (name.endsWith('png')) return 'png';
									};

									if (customerLegalDocuments && customerLegalDocuments.length) {
										customerLegalDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (
										customerBusinessDocuments &&
										customerBusinessDocuments.length
									) {
										customerBusinessDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (
										customerReferralDocuments &&
										customerReferralDocuments.length
									) {
										customerReferralDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (documents.length) {
										openDocumentModal(documents);
									} else {
										alert('no document found');
									}
								}}
							>
								View Dokumen
							</a>,
						],
						[
							'Data Pra-Survey',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openPraSurveyDataModal(prospectId);
								}}
							>
								Detail Pra-Survey
							</a>,
						],
					],
					[
						[
							'Analisa Pra-Survey',
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									openPraSurveyAnalysisModal(prospectId);
								}}
								className="trigger-popup"
							>
								Detail Analisa
							</a>,
						],
					],
				]}
				style={{ marginBottom: 20 }}
			/>

			<FleetTitle>Survey Information</FleetTitle>
			<DataRow
				items={[
					[
						['Department', surveyInformation?.department],
						['Nama FRM', mainInformation?.frm.value],
					],
					[
						[
							'Lokasi Survey Kantor',
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									openMapModal({
										title: 'Lokasi Survey Kantor',
										address: surveyInformation?.officeAddress,
										latitude: surveyInformation?.officeLat,
										longitude: surveyInformation?.officeLong,
										draggable: false,
										viewOnly: true,
									});
								}}
							>
								{surveyInformation?.officeAddress}
							</a>,
						],
						[
							'Tanggal Survey Kantor',
							dayjs(surveyInformation?.surveyOfficeDate).format('DD/MM/YYYY'),
						],
					],
					[
						[
							'Lokasi Survey Site',
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									openMapModal({
										title: 'Lokasi Survey Site',
										address: surveyInformation?.siteAddress,
										latitude: surveyInformation?.siteLat,
										longitude: surveyInformation?.siteLong,
										draggable: false,
										viewOnly: true,
									});
								}}
							>
								{surveyInformation?.siteAddress}
							</a>,
						],
						[
							'Tanggal Survey Site',
							dayjs(surveyInformation?.surveySiteDate).format('DD/MM/YYYY'),
						],
					],
					[
						['Peserta Survey (FRM)', mainInformation?.frm?.value],
						['Peserta Survey (CA)', mainInformation?.creditAnalyst?.value],
					],
				]}
				style={{ marginBottom: 20 }}
			/>
			<Divider />

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				onFinish={handleSubmit}
			>
				<Form.Item
					name={fieldName('prospectId')}
					initialValue={mainInformation?.prospectId}
					noStyle
				/>
				<Form.Item
					name={fieldName('participants')}
					initialValue={surveyParticipants?.map(
						(participant) => participant.id,
					)}
					noStyle
				>
					<Table
						scroll={{ x: 'max-content' }}
						dataSource={
							surveyParticipants?.map((record, key) => {
								return { ...record, key };
							}) ?? []
						}
						columns={[
							{
								title: 'No',
								render: (_, record) => record.key + 1,
							},
							{
								title: 'Nama Peserta Survey',
								dataIndex: ['name'],
							},
							{
								title: 'Jabatan Peserta Survey',
								dataIndex: ['position'],
							},
							{
								title: 'Approve',
								width: 1,
								align: 'center',
								render: (_, record) => {
									return (
										<Checkbox
											checked={value?.participants?.includes(record.id!)}
											onChange={(e) => {
												if (e.target.checked) {
													form.setFieldValue(fieldName('participants'), [
														...(value?.participants ?? []),
														record.id,
													]);
												} else {
													// remove index value from array participants
													form.setFieldValue(
														fieldName('participants'),
														value?.participants?.filter(
															(participant) => participant !== record.id,
														),
													);
												}
											}}
										/>
									);
								},
							},
						]}
						bordered
						pagination={false}
						style={{ marginBottom: 20 }}
					/>
				</Form.Item>
				<FleetTitle>Approval History</FleetTitle>
				<Table
					dataSource={
						approvalHistories?.map((record, key) => {
							return { ...record, key };
						}) ?? []
					}
					columns={[
						{
							title: 'No',
							width: 1,
							align: 'center',
							render: (_, __, index) => index + 1,
						},
						{
							title: 'Name',
							dataIndex: 'approvalBy',
						},
						{
							title: 'Date',
							dataIndex: 'approvalDate',
						},
						{
							title: 'Process',
							dataIndex: 'approvalProcess',
						},
						{
							title: 'Status',
							align: 'center',
							render: (_, record) => {
								return (
									<span
										className={`review-status ${record.approvalStatus.toLowerCase()}`}
									>
										{record.approvalStatus}
									</span>
								);
							},
						},
						{
							title: 'Approval Notes',
							dataIndex: 'approvalNote',
						},
					]}
					bordered
					pagination={false}
					style={{ marginBottom: 20 }}
				/>
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('approvalStatus')}
							label="Approval Status"
							rules={[rule.required()]}
						>
							<Select
								allowClear={true}
								options={[
									{ value: 'Approve' },
									// { value: 'Return' },
									{ value: 'Reject' },
								]}
							/>
						</Form.Item>
					</Col>
					{value?.approvalStatus === 'Reject' && (
						<Col span={12}>
							<Form.Item
								name={fieldName('rejectCategoryId')}
								label="Category Reject"
								rules={[rule.required()]}
							>
								<Select {...selectRejectCategory} />
							</Form.Item>
						</Col>
					)}
				</Row>
				<Divider />
				<Form.Item
					name={fieldName('approvalNotes')}
					label="Approval Notes"
					labelCol={{ span: 6 }}
					wrapperCol={{
						style: {
							color: 'blue',
							marginLeft: -10,
						},
					}}
					rules={[rule.required()]}
				>
					<TextArea rows={3} />
				</Form.Item>
				<Button
					loading={submitCHApprovalSurveyMutation.isPending}
					type="primary"
					htmlType="submit"
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
						marginTop: 40,
					}}
				>
					SAVE
				</Button>
			</Form>
		</Fragment>
	);
};

export default CHSurveyApprovalPage;
